import { ReactElement, useContext, useState } from 'react';
import { CTA } from '@volkswagen-onehub/components-core';
import { Linkedin, Mail, OpenInLayer, ShareAndroid, Twitter } from '@volkswagen-onehub/icons-core';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GiveawayResponse } from '@org-avp/avp-hck-dga-customerui-commons';
import { WhatsAppIcon } from '@/common/icons/WhatsAppIcon';
import { GiveawayContext } from '@/state/state';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { TrackingEvent } from '@/util/tracking/definitions';

const PopoverContainer = styled.div<{ isVin: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-content: center;
  text-align: center;
  margin-right: 0;
`;

const PopoverContent = styled.div<{ isVisible: boolean }>`
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  position: absolute;
  left: 50%;
  right: 0;
  width: ${(props) => props.theme.size.static530};
  overflow: hidden;
  bottom: ${(props) => props.theme.size.static520};
  background-color: white;
  border-radius: ${(props) => props.theme.size.static150};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: translateX(-50%);
  transition: opacity 0.1s ease-in;
  box-shadow: 0 10px 20px #0000004d;
`;

const PopoverArrow = styled.div<{ isVisible: boolean }>`
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
  position: absolute;
  display: block;
  width: 0;
  height: 0;
  left: 50%;
  bottom: ${(props) => props.theme.size.static510};
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid white;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.1s ease-in;

  &:after {
    content: '';
    position: absolute;
    background-color: transparent;
    width: ${(props) => props.theme.size.static530};
    height: ${(props) => props.theme.size.static200};
    bottom: -5px;
    transform: translateX(-50%);
    left: 0;
  }
`;

const PopoverItem = styled.a<{ backgroundColor: string }>`
  width: 100%;
  height: ${(props) => props.theme.size.static520};
  border: none;
  background-color: ${(props) => props.backgroundColor};
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  color: #000e26;
  cursor: pointer;

  &:hover {
    filter: brightness(80%);
  }
`;

const PopoverContentWrapper = styled.div`
  display: flex;
  height: 100%;
  position: relative;
`;

const text = `This is my VW Configuration: ${window.location.href}`;

enum ColorCodes {
  LINKEDIN = '#0a66c2',
  TWITTER = '#1877f2',
  WHATSAPP = '#25d366',
  DEFAULT = '#FFF',
}

enum SocialPlatforms {
  LINKEDIN = 'LinkedIn',
  TWITTER = 'Twitter (X)',
  WHATSAPP = 'WhatsApp',
  MAIL = 'E-Mail',
  COPY = 'Copy to Clipboard',
}

type Content = { id: number; icon: ReactElement; color: string; platform: string; link?: string };

export const SocialPopover = (): ReactElement => {
  const [isVisible, setVisible] = useState(false);
  const { t } = useTranslation();
  const trackingService = useTrackingService();
  const giveawayData: GiveawayResponse = useContext(GiveawayContext);
  const isVIN = giveawayData.marketingCode === 'VIN';

  const content: Content[] = [
    {
      id: 1,
      platform: SocialPlatforms.COPY,
      icon: (
        <OpenInLayer
          aria-label={t(`socialPopover.copy.ariaLabel`)}
          title={t(`socialPopover.copy.title`)}
          tabIndex={0}
        />
      ),
      color: ColorCodes.DEFAULT,
    },
    {
      id: 2,
      platform: SocialPlatforms.MAIL,
      icon: (
        <Mail
          aria-label={t(`socialPopover.mail.ariaLabel`)}
          title={t(`socialPopover.mail.title`)}
          tabIndex={0}
        />
      ),
      color: ColorCodes.DEFAULT,
      link: `mailto:?subject=${'This is my VW Configuration'}&body=${window.location.href}`,
    },
    {
      id: 3,
      platform: SocialPlatforms.WHATSAPP,
      icon: (
        <WhatsAppIcon
          ariaLabel={t(`socialPopover.whatsapp.ariaLabel`)}
          title={t(`socialPopover.whatsapp.title`)}
          tabIndex={0}
        />
      ),
      color: ColorCodes.WHATSAPP,
      link: `https://wa.me/?text=${encodeURIComponent(text)}`,
    },
    {
      id: 4,
      platform: SocialPlatforms.TWITTER,
      icon: (
        <Twitter
          aria-label={t(`socialPopover.twitter.ariaLabel`)}
          title={t(`socialPopover.twitter.title`)}
          tabIndex={0}
        />
      ),
      color: ColorCodes.TWITTER,
      link: `https://www.twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
    },
    {
      id: 5,
      platform: SocialPlatforms.LINKEDIN,
      icon: (
        <Linkedin
          aria-label={t(`socialPopover.linkedin.ariaLabel`)}
          title={t(`socialPopover.linkedin.title`)}
          tabIndex={0}
        />
      ),
      color: ColorCodes.LINKEDIN,
      link: `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`,
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (e) {
      console.error(e);
    }
  };

  const trackEvent = (platform: string) => {
    trackingService.track(TrackingEvent.VWBasicSocialShareClick, {
      social: { Platform: platform },
      eventInfo: { contentId: `clicked social share button: ${platform}` },
    });
  };

  return (
    <PopoverContainer
      isVin={isVIN}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      data-testid="popover-container"
    >
      <CTA
        emphasis="secondary"
        size="large"
        icon={<ShareAndroid aria-label={t('socialPopover.ariaLabel')} tabIndex={0} />}
        tag="a"
      >
        {t('socialPopover.title')}
      </CTA>
      <PopoverContentWrapper>
        <PopoverContent isVisible={isVisible} data-testid="popover-content">
          {content.map((links) =>
            !links.link ? (
              <PopoverItem
                key={links.id}
                backgroundColor={links.color}
                onClick={() => {
                  void copyToClipboard();
                  trackEvent(links.platform);
                }}
              >
                {links.icon}
              </PopoverItem>
            ) : (
              <PopoverItem
                key={links.id}
                backgroundColor={links.color}
                href={links.link}
                onClick={() => trackEvent(links.platform)}
                target="_blank"
              >
                {links.icon}
              </PopoverItem>
            ),
          )}
        </PopoverContent>
        <PopoverArrow isVisible={isVisible} />
      </PopoverContentWrapper>
    </PopoverContainer>
  );
};
